import {
    Box,
    Button,
    Grid,
    Typography,
    useTheme,
    Theme,
    Dialog,
    IconButton,
    DialogContent,
    DialogActions,
    CardMedia,
    Tooltip,
} from '@mui/material';
import { useHistory } from 'react-router';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Applicant, NonKYCVerifyPanRes, SubmitNonKYCApplicationToBSERes, VerifyPanResponseBody } from '../../redux-store/types/api-types';
import { ApplicationProps } from '../../redux-store/types/api-types';
import { RootStateType } from '../../redux-store/reducers';
import React from 'react';
import { DialogCloseIcon, InfoIconOutlined } from '../../assets/SvgIcons';
import { compare } from '../../utils/utilityFunctions';
import { Item, ItemDetails } from '../ApplicationDetails';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { getFormattedISTDateString } from '../../utils/date';
import { NonKYCVerifyPan, SubmitNonKYCApplicationToBSE, verifyPan } from '../../redux-store/actions/onBoarding';
import { showError } from '../../redux-store/actions/auth';

export function InitiateKYCDialog({ isDialogOpen, handleDialogClose, row, getApplication }: { isDialogOpen: boolean, handleDialogClose: () => void, row: ApplicationProps, getApplication: () => void }): JSX.Element {

    if (!isDialogOpen) return <></>;
    const theme = useTheme();
    const dispatch = useDispatch();
    const history = useHistory();

    const { company = "WMS" } = useSelector((store: RootStateType) => store.navbarInfo)
    const { currencyCode = "INR" } = useSelector((store: RootStateType) => store.currencyInfo);

    const [verifyPanLoading, setVerifyPanLoading] = useState<boolean>(false);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [panVerificationResponse, setPanVerificationResponse] = useState<NonKYCVerifyPanRes>();
    const { enqueueSnackbar } = useSnackbar();

    const [kraStatusList, setKraStatusList] = useState<{
        panNo: string;
        kraVerified: string;
        message: string
    }[]>(row.applicants.map((applicant) => ({ panNo: applicant.panNo ?? '', kraVerified: applicant.kraVerified ?? 'no', message: '' })));

    const handleVerifyPan = async (applicant: Partial<Applicant>) => {
        try {
            setVerifyPanLoading(true);
            const {
                panNo,
                dateOfBirth,
                name,
                id
            } = applicant;
            const response = (await dispatch(
                NonKYCVerifyPan({
                    applicationId: row.id,
                    panNo: panNo ?? '',
                    dateOfBirth: dateOfBirth ?? '',
                    name: name ?? '',
                    applicantId: id ?? '',
                })
            )) as unknown as NonKYCVerifyPanRes;

            if (!response) return;
            enqueueSnackbar(response.message, { variant: `${response.kraCheck ? 'success' : 'default'}` });

            setKraStatusList((prev) => prev.map((item) => item.panNo === panNo ? { ...item, kraVerified: response.application.applicants[0].kraVerified ?? 'no', message: response.message } : item));
            setPanVerificationResponse(response);
            getApplication();
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setVerifyPanLoading(false);
        }
    }

    const handleSubmitToBSE = async () => {
        try {
            setSubmitLoading(true);
            const response = (await dispatch(SubmitNonKYCApplicationToBSE({ applicationId: row.id ?? '' }))) as unknown as SubmitNonKYCApplicationToBSERes;
            if (!response) return;
            if (response.isSuccess) {
                enqueueSnackbar(response.message, { variant: 'success' });
                handleDialogClose();
                getApplication();
                return;
            } else {
                enqueueSnackbar(response.message, { variant: 'error' });
                getApplication();
                return;
            }
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setSubmitLoading(false);
        }
    }

    return (
        <Dialog
            // onClose={handleDialogClose}
            open={isDialogOpen}
            fullWidth
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '15px',
                    backgroundColor: 'background.default',
                    maxWidth: '700px',
                    padding: '25px',
                    m: 0,
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h2" sx={{ fontWeight: 600 }}>
                        BSE Progress Info
                    </Typography>
                    <IconButton onClick={handleDialogClose}>
                        <DialogCloseIcon />
                    </IconButton>
                </Box>
                {
                    <Box sx={{ mt: 2 }}>
                        {
                            row.applicants.map((applicant, index) => {
                                const currentPanStatus = kraStatusList.find((item) => item.panNo === applicant.panNo)?.kraVerified;
                                const currentapplicantObject = row.applicants.find((item) => item.panNo === applicant.panNo);
                                return (
                                    <Grid container spacing={2} key={index}>
                                        <Grid item xs={12} sm={6}>
                                            <Item sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                            })}>{'Investor Name'}</Item>
                                            <ItemDetails sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                                            })}> {row?.onboardingType === 'minor' ? (applicant?.minorName ?? '-') : (applicant?.name ?? '-')}</ItemDetails>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Item sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                            })}>{'Date of Birth'}</Item>
                                            <ItemDetails sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                                            })}> {row?.onboardingType === 'minor' ? (getFormattedISTDateString(applicant?.minorDOB) ?? '-') : (getFormattedISTDateString(applicant?.dateOfBirth) ?? '-')}</ItemDetails>
                                        </Grid>

                                        <Grid item xs={12} sm={6} sx={{ display: row?.onboardingType === 'minor' ? 'block' : 'none' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <Item sx={(theme: Theme) => ({
                                                    ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                                })}>{'Guardian Name'}</Item>
                                                <Tooltip title={'This name is used for KRA verification'} followCursor sx={{ mb: 1 }}>
                                                    <Typography variant='body_500' color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: '5px', ml: 0.5, mt: 1 }}><InfoIconOutlined /></Typography>
                                                </Tooltip>
                                            </Box>
                                            <ItemDetails sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                                            })}> {row?.onboardingType === 'minor' ? (applicant?.name ?? '-') : '-'}</ItemDetails>
                                        </Grid>

                                        <Grid item xs={12} sm={6} sx={{ display: row?.onboardingType === 'minor' ? 'block' : 'none' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <Item sx={(theme: Theme) => ({
                                                    ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                                })}>{'Guardian Date of Birth'}</Item>
                                               <Tooltip title={'This DOB is used for KRA verification'} followCursor sx={{ mb: 1 }}>
                                                    <Typography variant='body_500' color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: '5px', ml: 0.5, mt: 1 }}><InfoIconOutlined /></Typography>
                                                </Tooltip>
                                            </Box>
                                            <ItemDetails sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                                            })}> {row?.onboardingType === 'minor' ? (getFormattedISTDateString(applicant?.dateOfBirth) ?? '-') : '-'}</ItemDetails>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Item sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                            })}>{'Email'}</Item>
                                            <ItemDetails sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                                            })}> {(applicant?.email ?? '-')}</ItemDetails>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Item sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                            })}>{'Mobile'}</Item>
                                            <ItemDetails sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                                            })}> {(applicant?.mobile ?? '-')}</ItemDetails>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Item sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                            })}>{'Pan'}</Item>
                                            <ItemDetails sx={(theme: Theme) => ({
                                                ...theme.typography.body_600, color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                                            })}> {(applicant?.panNo ?? '-')}</ItemDetails>
                                        </Grid>

                                        <Grid container item xs={12} sm={6}>
                                            <Grid item xs={6}>
                                                <Item sx={(theme: Theme) => ({
                                                    ...theme.typography.body_600, lineHeight: '15px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '5px'
                                                })}>{'KRA Status'}</Item>
                                                <ItemDetails sx={(theme: Theme) => ({
                                                    ...theme.typography.body_600, color: compare(currentPanStatus, ['yes']) ? theme.palette.success.main : theme.palette.primary[500], textTransform: 'capitalize', fontWeight: '500'
                                                })}> {compare(currentPanStatus, ['yes']) ? 'PAN Verified' : 'Pending'}</ItemDetails>
                                                <Typography variant="body_500" sx={{ color: theme.palette.text.secondary, mt: 1 }}> {currentapplicantObject?.message ?? ''}</Typography>
                                            </Grid>
                                            <Grid item xs={6} sx={{ justifySelf: 'flex-end', alignSelf: 'flex-end', display: compare(currentPanStatus, ['yes']) ? 'none' : 'flex' }}>
                                                <LoadingButton
                                                    loadingPosition="start"
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleVerifyPan(applicant)}
                                                    fullWidth
                                                    sx={{
                                                        ...theme.typography.subtitle_400,
                                                        letterSpacing: 2,
                                                        fontWeight: 400,
                                                        height: '40px'
                                                    }}
                                                    disabled={compare(currentPanStatus, ['yes'])}
                                                    loading={verifyPanLoading}
                                                >
                                                    {compare(currentPanStatus, ['yes']) ? 'Verified' : 'Verify Pan'}
                                                </LoadingButton>
                                            </Grid>
                                        </Grid>


                                        <Grid item xs={12} sx={{ display: index == row.applicants.length - 1 ? 'none' : 'block' }}>
                                            <hr style={{ width: '100%', color: '#6C737F59' }}></hr>
                                        </Grid>
                                    </Grid>
                                )
                            })
                        }
                    </Box>
                }
            </DialogContent>
            <DialogActions sx={{ mt: 2, borderTop: '1px solid #6C737F59', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', width: '100%' }}>
                    {/* <Grid item xs={12} sm={6}>
                        <LoadingButton
                            loadingPosition="start"
                            type="submit"
                            variant="contained"
                            color="primary"
                            onClick={handleDialogClose}
                            fullWidth
                            sx={{
                                ...theme.typography.subtitle_400,
                                letterSpacing: 2,
                                fontWeight: 400,
                            }}
                        >
                            Verify Pan
                        </LoadingButton>
                    </Grid> */}
                    {/* <Grid item xs={12}> */}
                    {kraStatusList.every((item) => `${item.kraVerified ?? ''}`.toLowerCase() !== 'yes') &&
                        <Tooltip title={'KRA verification is Mandatory to sent to BSE'} followCursor sx={{ mb: 1 }}>
                            <Typography variant='body_500' color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: '5px', ml: 1, mt: 1 }}><InfoIconOutlined />Note</Typography>
                        </Tooltip>}
                    <LoadingButton
                        fullWidth
                        loadingPosition="start"
                        type="submit"
                        variant="contained"
                        color="primary"
                        onClick={handleSubmitToBSE}
                        sx={{
                            ...theme.typography.subtitle_400,
                            letterSpacing: 2,
                            fontWeight: 400,
                        }}
                        disabled={kraStatusList.some((item) => `${item.kraVerified ?? ''}`.toLowerCase() !== 'yes')}
                        loading={submitLoading}
                    >
                        Send to BSE
                    </LoadingButton>
                </Box>
            </DialogActions>
        </Dialog >
    );
}